/**
 * Employee statistics calculation from schedule results
 */

import type { ScheduleResult, EmployeeStats, ProjectTimeRange } from "./types";

/**
 * Berechnet Statistiken pro Mitarbeiter
 */
export function calculateEmployeeStats(
  schedule: ScheduleResult,
  tasks: { id: string; title: string }[] = []
): Map<string, EmployeeStats> {
  const taskMap = new Map(tasks.map(t => [t.id, t.title]));

  const stats = new Map<
    string,
    {
      totalBaseDays: number;
      totalEffectiveDays: number;
      multitaskingDays: number;
      themes: Set<string>;
      efficiency: number;
      projectTimeRanges: ProjectTimeRange[];
    }
  >();

  const initStats = () => ({
    totalBaseDays: 0,
    totalEffectiveDays: 0,
    multitaskingDays: 0,
    themes: new Set<string>(),
    efficiency: 1,
    projectTimeRanges: [] as ProjectTimeRange[],
  });

  for (const [taskId, scheduled] of schedule.scheduledTasks) {
    const taskTitle = taskMap.get(taskId) || taskId;

    // Stats für den Hauptverantwortlichen (erste Person)
    const empId = scheduled.primaryAssigneeId;

    if (!stats.has(empId)) {
      stats.set(empId, initStats());
    }

    const empStats = stats.get(empId)!;
    empStats.totalBaseDays += scheduled.baseDays;
    empStats.totalEffectiveDays += scheduled.effectiveDays;
    empStats.multitaskingDays += scheduled.multitaskingDayCount;
    empStats.themes.add(scheduled.column);
    empStats.projectTimeRanges.push({
      taskId,
      taskTitle,
      column: scheduled.column,
      startDay: scheduled.startDay,
      endDay: scheduled.endDay,
      isPrimary: true,
    });

    // Auch für zusätzliche Teammitglieder Stats erfassen
    for (let i = 1; i < scheduled.assigneeIds.length; i++) {
      const helperEmpId = scheduled.assigneeIds[i];
      if (!stats.has(helperEmpId)) {
        stats.set(helperEmpId, initStats());
      }
      const helperStats = stats.get(helperEmpId)!;
      helperStats.themes.add(scheduled.column);
      helperStats.totalBaseDays += scheduled.baseDays;
      helperStats.totalEffectiveDays += scheduled.effectiveDays;
      helperStats.multitaskingDays += scheduled.multitaskingDayCount;
      helperStats.projectTimeRanges.push({
        taskId,
        taskTitle,
        column: scheduled.column,
        startDay: scheduled.startDay,
        endDay: scheduled.endDay,
        isPrimary: false,
      });
    }
  }

  // Berechne Effizienz und konvertiere zu Ergebnis
  const result = new Map<string, EmployeeStats>();

  for (const [empId, empStats] of stats) {
    const efficiency =
      empStats.totalEffectiveDays > 0
        ? empStats.totalBaseDays / empStats.totalEffectiveDays
        : 1;

    const sortedRanges = [...empStats.projectTimeRanges].sort((a, b) => a.startDay - b.startDay);

    result.set(empId, {
      totalBaseDays: empStats.totalBaseDays,
      totalEffectiveDays: empStats.totalEffectiveDays,
      multitaskingDays: empStats.multitaskingDays,
      themes: Array.from(empStats.themes),
      efficiency,
      projectTimeRanges: sortedRanges,
    });
  }

  return result;
}
